import type { StoreProvider, StoreProviders } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import type { LoadStatus } from "../primitives/load-status";

type StripeMode = StoreProviders["stripeMode"];

/**
 * The selected shop's commerce and payment connections, with the live status
 * the provider reports. Shown in three places (Settings, the payments step, the
 * setup checklist), so it is cached here and marked stale by whatever changes a
 * connection; the shell refreshes it on a slow poll, since Stripe finishes a
 * review without telling this app.
 */
export class ProvidersRepository extends ChangeNotifier {
  #storeId: string | null = null;
  #status: LoadStatus = { kind: "idle" };
  #providers: readonly StoreProvider[] = [];
  #stripeMode: StripeMode = "live";
  #loadedStoreId: string | null = null;
  #stale = false;

  get storeId(): string | null {
    return this.#storeId;
  }

  get status(): LoadStatus {
    return this.#status;
  }

  get providers(): readonly StoreProvider[] {
    return this.#providers;
  }

  get stripeMode(): StripeMode {
    return this.#stripeMode;
  }

  get isStale(): boolean {
    return this.#stale;
  }

  find(providerKey: string): StoreProvider | null {
    return this.#providers.find((p) => p.providerKey === providerKey) ?? null;
  }

  /** The shop has answered at least once; a refetch in flight keeps the last answer on screen. */
  isLoadedFor(storeId: string): boolean {
    return this.#storeId === storeId && this.#loadedStoreId === storeId;
  }

  setLoading(storeId: string): void {
    if (this.#storeId !== storeId) {
      this.#providers = [];
      this.#loadedStoreId = null;
    }
    this.#storeId = storeId;
    this.#stale = false;
    this.#set({ kind: "loading" });
  }

  setProviders(storeId: string, providers: readonly StoreProvider[], stripeMode: StripeMode = "live"): void {
    this.#storeId = storeId;
    this.#loadedStoreId = storeId;
    this.#providers = [...providers];
    this.#stripeMode = stripeMode;
    this.#set({ kind: "loaded" });
  }

  setError(storeId: string, message: string): void {
    this.#storeId = storeId;
    this.#set({ kind: "error", message });
  }

  /** A connect, refresh or disconnect happened: whoever shows the list should fetch again. */
  markStale(): void {
    if (this.#stale) return;
    this.#stale = true;
    this.notifyListeners();
  }

  reset(): void {
    this.#storeId = null;
    this.#loadedStoreId = null;
    this.#providers = [];
    this.#stale = false;
    this.#set({ kind: "idle" });
  }

  #set(status: LoadStatus): void {
    this.#status = status;
    this.notifyListeners();
  }
}
